import type { FC, ReactNode } from 'react';
import { Box } from '@mui/material';

import { ReactComponent as CheckIcon } from 'assets/images/check-circle-filled-icon.svg';
import { ReactComponent as AlertIcon } from 'assets/images/ic-circle-alert.svg';
import { Text } from 'ui';
import palette from 'ui/theme/palette';
import type { SectionWeightSummary } from './section-weight-summary.util';

export interface SectionWeightPanelProps {
  summary: SectionWeightSummary;
  children: ReactNode;
}

// The gutter left by each row's action icons plus its right padding. Measured off a rendered row —
// keep it in step with field-display's Paper padding and icon set.
const FIELD_ACTIONS_GUTTER = 129;

// The weight input's own width, so the rule below spans exactly the numbers it totals.
const WEIGHT_INPUT_WIDTH = 64;

// Matches the total's 20px text line.
const STATUS_ICON_SIZE = 20;

/** Border / background / text per weight state, shared with the scoring rail. */
export const WEIGHT_STATE_STYLES = {
  balanced: { border: palette.primary.main, background: palette.primary.screen, text: palette.primary.main },
  under: { border: palette.warning.border, background: palette.warning.screen, text: palette.warning.text },
  over: { border: palette.error.border, background: palette.background.error, text: palette.error.dark }
};

/** Long-addition style validation container for a scored section's question weights. */
export const SectionWeightPanel: FC<SectionWeightPanelProps> = ({ summary, children }) => {
  const styles = WEIGHT_STATE_STYLES[summary.state];

  let statusMessage = '';
  if (summary.state === 'under') {
    statusMessage = `${summary.difference}% still to assign`;
  } else if (summary.state === 'over') {
    statusMessage = `${summary.difference}% too much`;
  }

  return (
    <Box
      data-cy="section-weight-panel"
      data-state={summary.state}
      sx={{
        border: `1px solid ${styles.border}`,
        background: styles.background,
        borderRadius: '8px',
        p: 2,
        width: '100%'
      }}
    >
      {children}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1.5, mt: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {statusMessage && (
            <Text
              content={statusMessage}
              type="body3"
              sx={{ color: styles.text, fontWeight: 'bold' }}
              dataCy="section-weight-status-message"
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
          {/* Mirrors the weight cell's gap and hidden '%', so the rule lands on the input column. */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
            <Box
              data-cy="section-weight-rule"
              sx={{
                borderTop: `2px solid ${styles.text}`,
                pt: 0.5,
                width: `${WEIGHT_INPUT_WIDTH}px`,
                textAlign: 'right'
              }}
            >
              <Text
                content={`${summary.total}%`}
                type="body2"
                sx={{ color: styles.text, fontWeight: 'bold' }}
                dataCy="section-weight-total"
              />
            </Box>
            <Text inline content="%" type="body3" sx={{ visibility: 'hidden' }} />
          </Box>
          {/* Sits in the gutter left by the row action icons, which keeps the total aligned to the inputs. */}
          <Box
            sx={{
              width: `${FIELD_ACTIONS_GUTTER}px`,
              display: 'flex',
              justifyContent: 'flex-start',
              // Centred on the rule-and-total block; the digits alone would hang it below their baseline.
              alignSelf: 'stretch',
              alignItems: 'center'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexShrink: 0,
                // Recolour the alert icon, which ships in primary green; the check icon is already correct.
                ...(summary.state === 'balanced' ? {} : { path: { fill: styles.text } })
              }}
              data-cy="section-weight-status-icon"
              data-state={summary.state}
            >
              {summary.state === 'balanced' ? (
                <CheckIcon width={STATUS_ICON_SIZE} height={STATUS_ICON_SIZE} />
              ) : (
                <AlertIcon width={STATUS_ICON_SIZE} height={STATUS_ICON_SIZE} />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
